import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import eventStream from "@/lib/eventStream";

/**
 * Queue-health data: eager fetch + live SSE updates via the shared bus
 * (`queue_health`, `rewrite_savings`), polling fallback, and the sweeper trigger.
 */
export function useQueueHealth() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);
  const [sweepResult, setSweepResult] = useState(null);
  const [liveState, setLiveState] = useState("connecting");
  const [savings, setSavings] = useState(null); // {date, by_reason, total_skipped, est_savings_usd}

  const fetchHealth = useCallback(async () => {
    try {
      const r = await api.get("/admin/queue-health");
      setData(r.data); setErr(""); setLoading(false);
      return r.data;
    } catch (e) {
      setErr(e.response?.data?.detail || e.message); setLoading(false);
      throw e;
    }
  }, []);

  useEffect(() => {
    fetchHealth(); // eager paint so we don't flash empty during the handshake
    const unsub = eventStream.subscribe(
      "queue_health",
      (snap) => { setData(snap); setErr(""); setLoading(false); },
      fetchHealth, // polling fallback if SSE permanently fails
    );
    const unsubSavings = eventStream.subscribe(
      "rewrite_savings",
      setSavings,
      async () => (await api.get("/admin/rewrite-skips/today")).data,
    );
    const unsubLive = eventStream.onLiveStateChange(setLiveState);
    return () => { unsub(); unsubSavings(); unsubLive(); };
  }, [fetchHealth]);

  async function runSweepers() {
    setSweeping(true); setSweepResult(null);
    try {
      const r = await api.post("/admin/lifecycle/freshness/run");
      setSweepResult({
        deleted: r.data?.pending?.deleted ?? 0,
        archived: r.data?.ready?.archived ?? 0,
        at: new Date().toISOString(),
      });
      await fetchHealth(); // show the post-sweep snapshot instantly, before the next stream tick
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setSweeping(false); }
  }

  return { data, err, loading, sweeping, sweepResult, liveState, savings, fetchHealth, runSweepers };
}
